/**
 * Intelligent Loading Service
 * Consolidates smart loading + SVG path loading with navigation-aware caching
 * Prioritizes hero scene → parallax scenes → homepage listings
 */

import parallaxService from './parallaxService';
import databaseService from './databaseService';
import apiClient from './apiClient';

import logger from '../utils/logger.js';
class IntelligentLoadingService {
  constructor() {
    this.persistentCache = new Map(); // In-memory cache survives navigation
    this.sessionCache = this.loadSessionCache(); // Restore from sessionStorage
    this.imageCache = new Map(); // Loaded images cache
    this.loadingCache = new Map(); // Track loading status by URL
    this.activeLoads = new Set(); // Track currently loading URLs
    this.maxConcurrentLoads = 6; // Limit concurrent image loads
    this.listeners = new Map(); // Progress listeners by component
    this.eventListeners = new Map(); // Event listeners for data refresh

    this.isProduction =
      process.env.NODE_ENV === 'production' || window.location.hostname !== 'localhost';

    // Cache configuration with different TTL for different data types
    this.cacheConfig = {
      heroStickers: { ttl: 10 * 60 * 1000, persist: true }, // 10 min, critical
      homePageListings: { ttl: 5 * 60 * 1000, persist: true }, // 5 min, critical
      parallaxScenes: { ttl: 15 * 60 * 1000, persist: true }, // 15 min, stable
      parallaxData: { ttl: 15 * 60 * 1000, persist: true }, // 15 min, stable
      userPreferences: { ttl: 24 * 60 * 60 * 1000, persist: true }, // 24 hours
    };
  }

  /**
   * Load session cache from sessionStorage
   */
  loadSessionCache() {
    try {
      const cache = {};
      if (this.cacheConfig && typeof this.cacheConfig === 'object') {
        Object.keys(this.cacheConfig).forEach((key) => {
          const stored = sessionStorage.getItem(`clothedEye_${key}`);
          if (stored) {
            cache[key] = JSON.parse(stored);
          }
        });
      }
      return cache;
    } catch (error) {
      logger.warn('Failed to load session cache:', error);
      return {};
    }
  }

  /**
   * Check if we have sufficient cached data for instant homepage display
   */
  hasValidCachedData() {
    const heroData = this.getCachedData('heroStickers');
    const listingsData = this.getCachedData('homePageListings');

    // We need at least hero data and some listings for instant display
    return heroData && listingsData && Array.isArray(listingsData) && listingsData.length > 0;
  }

  /**
   * Get cached data with TTL check
   */
  getCachedData(key, customMaxAge = null) {
    const config = this.cacheConfig[key];
    const maxAge = customMaxAge || (config ? config.ttl : 5 * 60 * 1000);

    // Check memory cache first (fastest)
    const memoryData = this.persistentCache.get(key);
    if (memoryData && Date.now() - memoryData.timestamp < maxAge) {
      return memoryData.data;
    }

    // Check session storage (survives page refresh)
    const sessionData = this.sessionCache[key];
    if (sessionData && Date.now() - sessionData.timestamp < maxAge) {
      // Restore to memory cache
      this.persistentCache.set(key, sessionData);
      return sessionData.data;
    }

    return null; // Cache miss
  }

  /**
   * Persist critical data to both memory and session storage
   */
  persistCriticalData(key, data) {
    const cacheEntry = { data, timestamp: Date.now() };

    // Always store in memory
    this.persistentCache.set(key, cacheEntry);

    // Store in session if configured to persist
    const config = this.cacheConfig[key];
    if (config && config.persist) {
      try {
        // Check if the data is too large for sessionStorage
        const serialized = JSON.stringify(cacheEntry);
        const sizeInMB = new Blob([serialized]).size / (1024 * 1024);

        if (sizeInMB > 4) {
          // SessionStorage limit is usually ~5-10MB
          // Data too large for sessionStorage, storing in memory only
          return;
        }

        sessionStorage.setItem(`clothedEye_${key}`, serialized);
      } catch (error) {
        if (error.name === 'QuotaExceededError') {
          logger.warn(`SessionStorage quota exceeded for ${key}, clearing old cache and retrying`);
          // Try to clear old cache entries and retry
          this.clearOldSessionCache();
          try {
            sessionStorage.setItem(`clothedEye_${key}`, JSON.stringify(cacheEntry));
          } catch (retryError) {
            logger.warn(`Failed to persist ${key} even after clearing cache:`, retryError);
          }
        } else {
          logger.warn(`Failed to persist ${key} to sessionStorage:`, error);
        }
      }
    }
  }

  /**
   * Critical first load - hero scene priority
   */
  async loadCriticalFirst() {
    try {
      // Check client cache first
      const cachedHeroData = this.getCachedData('heroStickers');
      if (cachedHeroData) {
        return { heroStickers: cachedHeroData, fromCache: true };
      }

      // Load svgPath-only stickers (much smaller payload ~50-80 vs 312 items)
      // This also benefits from server-side caching in Netlify Functions/PHP
      const result = await databaseService.getSvgPathStickers();
      const svgPathStickers = result.data || [];

      // Cache the hero data on client
      this.persistCriticalData('heroStickers', svgPathStickers);

      // NOTE: Removed bulk hero image preloading to reduce initial load time
      // Hero scene cards will now load their own SVG assets on-demand

      return { heroStickers: svgPathStickers, fromCache: false };
    } catch (error) {
      console.error('Critical loading failed:', error);
      // Return cached data if available, even if expired
      const cachedHeroData = this.getCachedData('heroStickers', Infinity);
      return { heroStickers: cachedHeroData || [], fromCache: true, error };
    }
  }

  /**
   * Load homepage listings with caching
   * @param {Object} options - Loading options
   * @param {Function} onProgress - Optional callback for progress updates
   */
  async loadHomepageListings(options = {}, onProgress = null) {
    const cacheKey = 'homePageListings';

    try {
      // Check cache first
      const cachedListings = this.getCachedData(cacheKey);
      if (cachedListings && Array.isArray(cachedListings) && cachedListings.length > 0) {
        // Report instant completion for cached data
        if (onProgress) {
          onProgress(1, 1, `database-complete-${cachedListings.length}`);
        }

        // Start background refresh
        this.refreshListingsInBackground();
        return cachedListings;
      }

      // Report start of loading - NO FAKE PROGRESS
      if (onProgress) {
        onProgress(0, 1, 'database-start');
      }

      // Load fresh data
      const defaultOptions = {
        sortBy: 'sales',
        sortOrder: 'desc',
        limit: 999, // Load all stickers for complete grid display
      };

      // REAL loading - no simulation
      const startTime = Date.now();
      const result = await databaseService.getUnifiedStickers({ ...defaultOptions, ...options });
      const listings = result.data || [];
      const loadTime = Date.now() - startTime;

      logger.log(`📦 Database loaded ${listings.length} stickers in ${loadTime}ms`);

      // Report ACTUAL completion with REAL sticker count
      if (onProgress) {
        onProgress(1, 1, `database-complete-${listings.length}`);
      }

      // Cache the listings
      this.persistCriticalData(cacheKey, listings);

      return listings;
    } catch (error) {
      console.error('Homepage listings loading failed:', error);

      // Report completion even on error
      if (onProgress) {
        onProgress(1, 1, 'database-error');
      }

      // Return cached data if available, even if expired
      const cachedListings = this.getCachedData(cacheKey, Infinity);
      return cachedListings || [];
    }
  }

  /**
   * Background refresh without affecting UI
   */
  async refreshListingsInBackground() {
    try {
      const result = await databaseService.getUnifiedStickers({
        sortBy: 'sales',
        sortOrder: 'desc',
        limit: 999,
      });
      const freshData = result.data || [];

      // Update cache
      this.persistCriticalData('homePageListings', freshData);

      // Emit event for components to update if needed
      this.emit('dataRefreshed', { type: 'listings', data: freshData });
    } catch (error) {
      logger.warn('Background refresh failed:', error);
    }
  }

  /**
   * Progressive content loading (parallax scenes, additional data)
   */
  async loadProgressiveContent() {
    try {
      // Load parallax data in background
      const cachedParallaxData = this.getCachedData('parallaxData');
      if (!cachedParallaxData) {
        const activeScenes = await this.getActiveParallaxScenes();
        const smartData = await this.callSmartEndpoint(activeScenes);
        const processedData = this.processSmartData(smartData, activeScenes);

        this.persistCriticalData('parallaxData', processedData);

        // NOTE: Removed bulk parallax image preloading to reduce initial load time
        // Parallax components will load their own images on-demand
      }

      return true;
    } catch (error) {
      console.error('Progressive content loading failed:', error);
      return false;
    }
  }

  /**
   * Get list of active parallax scenes from category assignments
   */
  async getActiveParallaxScenes() {
    try {
      const assignments = await parallaxService.getAllCategorySceneAssignments();
      const activeScenes = assignments.map((assignment) => assignment.scene_name);
      const uniqueScenes = [...new Set(activeScenes)];
      return uniqueScenes;
    } catch (error) {
      console.error('Error getting active scenes:', error);
      return ['home-dino1', 'scooby2']; // Fallback to known scenes
    }
  }

  /**
   * Call the smart backend endpoint
   */
  async callSmartEndpoint(activeScenes) {
    const payload = {
      activeScenes: activeScenes,
      heroTarget: 30, // How many items the hero scene wants
    };
    const result = await apiClient.requestJson({
      path: '/smart-loading/parallax-first',
      method: 'POST',
      body: payload,
    });
    return result;
  }

  /**
   * Process smart data into component-ready format
   */
  processSmartData(smartData, activeScenes) {
    // Index parallax stickers by listing ID for fast lookup
    const parallaxIndex = {};
    if (smartData.parallax) {
      smartData.parallax.forEach((sticker) => {
        parallaxIndex[sticker.etsyListingId] = sticker;
      });
    }

    // Process scene details with sticker data
    const scenesWithData = {};
    if (smartData.sceneDetails) {
      Object.entries(smartData.sceneDetails).forEach(([sceneName, sceneData]) => {
        const sceneStickers = JSON.parse(sceneData.scene_stickers || '[]');

        // Attach full sticker data to each scene sticker
        const enrichedStickers = sceneStickers
          .map((sceneSticker) => {
            const fullStickerData = parallaxIndex[sceneSticker.listingId];
            if (fullStickerData) {
              return {
                ...fullStickerData,
                // Overlay scene-specific data (position, size from scene)
                ...sceneSticker,
              };
            }
            return sceneSticker; // Fallback if sticker data not found
          })
          .filter(Boolean);

        scenesWithData[sceneName] = {
          ...sceneData,
          enrichedStickers,
          originalSceneStickers: sceneStickers,
        };
      });
    }

    return {
      // For HeroScene component
      heroStickers: smartData.hero || [],

      // For ParallaxComponent components
      parallaxStickers: parallaxIndex,
      scenesWithData,

      // For debugging and metrics
      loadingStats: {
        parallaxItems: smartData.parallax?.length || 0,
        heroItems: smartData.hero?.length || 0,
        totalScenes: activeScenes.length,
        loadedScenes: Object.keys(scenesWithData).length,
      },
    };
  }

  /**
   * Extract SVG paths from stickers array
   */
  extractSvgPaths(stickers) {
    if (!Array.isArray(stickers)) return [];

    const paths = stickers
      .map((sticker) => sticker.svgPath)
      .filter((path) => path && path.trim() !== '');

    return [...new Set(paths)]; // Remove duplicates
  }

  /**
   * Extract parallax images from processed data
   */
  extractParallaxImages(processedData) {
    const images = [];

    if (processedData.parallaxStickers) {
      Object.values(processedData.parallaxStickers).forEach((sticker) => {
        if (sticker.svgPath) images.push(sticker.svgPath);
      });
    }

    if (processedData.scenesWithData) {
      Object.values(processedData.scenesWithData).forEach((scene) => {
        if (scene.enrichedStickers) {
          scene.enrichedStickers.forEach((sticker) => {
            if (sticker.svgPath) images.push(sticker.svgPath);
          });
        }
      });
    }

    return [...new Set(images)]; // Remove duplicates
  }

  /**
   * Load images with priority and concurrency control
   */
  async loadImagesWithPriority(imageUrls, priority = 'normal') {
    const validUrls = imageUrls.filter((url) => url && url.trim() !== '');

    if (validUrls.length === 0) {
      return { loaded: new Set(), failed: new Set() };
    }

    const loaded = new Set();
    const failed = new Set();

    // Filter out already loaded/cached images
    const urlsToLoad = validUrls.filter((url) => {
      if (this.imageCache.has(url)) {
        loaded.add(url);
        return false;
      }
      if (this.loadingCache.get(url) === 'failed') {
        failed.add(url);
        return false;
      }
      return true;
    });

    if (urlsToLoad.length === 0) {
      return { loaded, failed };
    }

    // Load images with concurrency control
    const loadPromises = urlsToLoad.map(async (url) => {
      // Wait for available slot if at max concurrent loads
      while (this.activeLoads.size >= this.maxConcurrentLoads) {
        await new Promise((resolve) => setTimeout(resolve, 10));
      }

      try {
        this.activeLoads.add(url);
        this.loadingCache.set(url, 'loading');

        const image = await this.loadSingleImage(url);

        this.imageCache.set(url, image);
        this.loadingCache.set(url, 'loaded');
        loaded.add(url);
      } catch (error) {
        logger.warn(`Failed to load image: ${url}`, error);
        this.loadingCache.set(url, 'failed');
        failed.add(url);
      } finally {
        this.activeLoads.delete(url);
      }
    });

    await Promise.all(loadPromises);
    return { loaded, failed };
  }

  /**
   * Load a single image and return a promise
   */
  loadSingleImage(url) {
    // Check cache first
    if (this.imageCache.has(url)) {
      return Promise.resolve(this.imageCache.get(url));
    }

    return new Promise((resolve, reject) => {
      const img = new Image();

      // Add timeout to prevent hanging
      const timeout = setTimeout(() => {
        reject(new Error(`Image load timeout: ${url}`));
      }, 10000); // 10 second timeout

      img.onload = () => {
        clearTimeout(timeout);
        resolve(img);
      };

      img.onerror = () => {
        clearTimeout(timeout);
        reject(new Error(`Failed to load image: ${url}`));
      };

      img.src = url;
    });
  }

  /**
   * Check if an image is loaded and ready
   */
  isImageLoaded(url) {
    return this.imageCache.has(url) || this.loadingCache.get(url) === 'loaded';
  }

  /**
   * Check if an image is currently loading
   */
  isImageLoading(url) {
    return this.loadingCache.get(url) === 'loading';
  }

  /**
   * Check if an image failed to load
   */
  isImageFailed(url) {
    return this.loadingCache.get(url) === 'failed';
  }

  /**
   * Get loading status for an image
   */
  getImageStatus(url) {
    if (this.imageCache.has(url)) return 'loaded';
    return this.loadingCache.get(url) || 'pending';
  }

  /**
   * Event system for data refresh notifications
   */
  on(event, callback) {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, new Set());
    }
    this.eventListeners.get(event).add(callback);

    return () => {
      const listeners = this.eventListeners.get(event);
      if (listeners) {
        listeners.delete(callback);
      }
    };
  }

  emit(event, data) {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.forEach((callback) => {
        try {
          callback(data);
        } catch (error) {
          logger.warn(`Event listener error for ${event}:`, error);
        }
      });
    }
  }

  /**
   * Clear old session cache entries to free up space
   */
  clearOldSessionCache() {
    try {
      Object.keys(this.cacheConfig).forEach((key) => {
        sessionStorage.removeItem(`clothedEye_${key}`);
      });
    } catch (error) {
      logger.warn('Failed to clear old session cache:', error);
    }
  }

  /**
   * Clear cache (useful for testing or memory management)
   */
  clearCache() {
    this.persistentCache.clear();
    this.imageCache.clear();
    this.loadingCache.clear();
    this.activeLoads.clear();

    // Clear session storage
    this.clearOldSessionCache();
  }

  /**
   * Get cache statistics for debugging
   */
  getCacheStats() {
    return {
      persistentCache: this.persistentCache.size,
      imageCache: this.imageCache.size,
      sessionCache: Object.keys(this.sessionCache).length,
      activeLoads: this.activeLoads.size,
      loadingStatus: {
        loaded: Array.from(this.loadingCache.values()).filter((status) => status === 'loaded')
          .length,
        loading: Array.from(this.loadingCache.values()).filter((status) => status === 'loading')
          .length,
        failed: Array.from(this.loadingCache.values()).filter((status) => status === 'failed')
          .length,
      },
    };
  }

  /**
   * Start loading grid images after modal is ready
   * Waits for modalImagesReady event, then loads grid sequentially
   */
  startGridLoadingAfterModal(stickers) {
    logger.log('🎯 Grid loading queued - waiting for modal images');

    const handleModalReady = () => {
      logger.log('✅ Modal images ready - starting grid load');

      // Load grid images top-to-bottom with priority queue
      this.loadGridImagesSequentially(stickers);

      // Remove listener
      window.removeEventListener('modalImagesReady', handleModalReady);
    };

    window.addEventListener('modalImagesReady', handleModalReady);
  }

  /**
   * Load grid images sequentially (top to bottom in display order)
   * Non-blocking - runs in background
   */
  async loadGridImagesSequentially(stickers) {
    if (!Array.isArray(stickers) || stickers.length === 0) {
      logger.warn('⚠️ No stickers to load for grid');
      return;
    }

    logger.log(`📸 Starting sequential grid load: ${stickers.length} stickers`);

    let loadedCount = 0;
    let skippedCount = 0;

    for (const sticker of stickers) {
      // Extract image URL
      const imageUrl = sticker.svgPath || sticker.first_image_url || sticker.image;

      if (!imageUrl) {
        skippedCount++;
        continue;
      }

      // Skip if already loaded or failed
      if (this.isImageLoaded(imageUrl) || this.isImageFailed(imageUrl)) {
        skippedCount++;
        continue;
      }

      // Load image
      try {
        await this.loadSingleImage(imageUrl);
        this.imageCache.set(imageUrl, true);
        this.loadingCache.set(imageUrl, 'loaded');
        loadedCount++;

        if (loadedCount % 10 === 0) {
          logger.log(`📸 Grid progress: ${loadedCount}/${stickers.length} loaded`);
        }
      } catch (error) {
        this.loadingCache.set(imageUrl, 'failed');
        logger.warn(`❌ Grid image failed: ${sticker.title || 'Unknown'}`);
      }

      // Small delay to prevent blocking
      await new Promise((resolve) => setTimeout(resolve, 10));
    }

    logger.log(`✅ Grid load complete: ${loadedCount} loaded, ${skippedCount} skipped`);
  }
}

export default new IntelligentLoadingService();
